import { ROUTE_PRIVACY_ZONE_M, type PublicUser, type RunDetailDto, type TerritoryDto } from '@rw/shared';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { Page } from '../../components/Layout';
import { ErrorState, LoadingState } from '../../components/states';
import { Avatar, Button, Card, SectionTitle, StatusPill } from '../../components/ui';
import { useLoadedRegion, useRun } from '../../hooks/queries';
import { useRuntime } from '../../hooks/useRuntime';
import { ApiError } from '../../lib/api';
import { formatDistance, formatDuration, formatNumber, formatPace, formatRunWhen, runTitle } from '../../lib/format';
import { cellsAlongRoute, trimRouteEnds } from '@rw/geo';
import type { LocalRun } from '../../services/run-store';
import { useSync } from '../../services/sync';
import { useAuth } from '../../stores/auth';
import { useNetwork } from '../../stores/network';
import { GameMap } from '../map/GameMap';
import { mapIdle, snapshotMap, type MapHandle } from '../map/map-lib';
import { drawRunImage } from './share-image';
import { ShareRunSheet } from './ShareRunSheet';
import { toast } from '../../stores/toasts';
import { RunComments } from './RunComments';
import { RunKudos } from './RunKudos';
import { UnlockedCard } from '../achievements/UnlockedCard';
import { DeleteRunSheet } from './DeleteRunSheet';
import { MY_COLOR } from '../../lib/colors';
import { appScroller } from '../../lib/app-scroller';
import { useT } from '../../i18n';

/** The detail map shows only this run's cells, not current ownership. */
const NO_OWNERS = new Map<string, TerritoryDto>();

const STATUS_TEXT: Record<string, string> = {
  VALID: 'Validated by the server. XP and territories are yours.',
  SUSPICIOUS: 'This run looks unusual. It is saved, but does not count for XP, territories or rankings.',
  REJECTED: 'This run was rejected by anti-cheat and does not count.',
  OUTSIDE_REGION: 'This run is outside the game area, so it does not count.',
  PENDING: 'Waiting for validation.',
};

export function RunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const fresh = !!(useLocation().state as { fresh?: boolean } | null)?.fresh;
  const navigate = useNavigate();
  const { runtime, region: currentRegion, lastResult } = useRuntime();
  const online = useNetwork((s) => s.online);
  const sync = useSync();
  const me = useAuth((s) => s.user?.id ?? null);
  const [local, setLocal] = useState<LocalRun | null | undefined>(undefined);
  // A "taken from" group shown alone on the map: a runner's id, or NEUTRAL.
  const [focus, setFocus] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const t = useT();
  const hideRouteEnds = useAuth((s) => s.user?.settings.hideRouteEnds ?? false);
  const [mapHandle, setMapHandle] = useState<MapHandle | null>(null);
  // While the share picture is taken: the route without its private ends (when the runner hides them).
  const [shareRoute, setShareRoute] = useState<Array<Array<[number, number]>> | null>(null);
  const [shareImage, setShareImage] = useState<Blob | null>(null);
  const [sharing, setSharing] = useState(false);

  // Local copy: lets the summary render instantly and offline, before the server answers.
  useEffect(() => {
    if (!runtime || !id) return;
    let alive = true;
    const load = () => runtime.store.getRun(id).then((r) => alive && setLocal(r ?? null));
    void load();
    const t = setInterval(load, 2000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [runtime, id, lastResult]);

  const serverRun = useRun(id);
  const notOnServerYet = serverRun.error instanceof ApiError && serverRun.error.status === 404;
  useEffect(() => {
    if (local?.status === 'synced') void serverRun.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local?.status]);

  const run: RunDetailDto | null = serverRun.data && serverRun.data.status !== 'PENDING' ? serverRun.data : (local?.result?.run ?? null);
  // The run's own city (its grid and area), whichever city the app is showing now.
  const region = useLoadedRegion(run?.regionId) ?? currentRegion;

  if (!run && local === undefined) return <LoadingState />;

  if (!run) {
    if (local && local.status !== 'synced') {
      const d = formatDistance(local.clientDistanceM);
      const failed = local.status === 'failed';
      return (
        <Page>
          <Header title={t('Run saved')} onBack={() => navigate('/')} />
          <Card className="mt-4 text-center" >
            <StatusPill status={failed ? 'REJECTED' : 'LOCAL'} />
            <div className="mt-3 font-display text-6xl font-extrabold tabular" data-testid="local-distance">
              {d.value}
              <span className="ml-1 text-2xl text-ink-400">{d.unit}</span>
            </div>
            <div className="mt-1 text-ink-400">{formatDuration(local.activeMs / 1000)}</div>
            <p className="mt-4 text-sm text-ink-300" data-testid="sync-pending">
              {failed
                ? t('The server refused this run: {error}', { error: local.error ?? t('unknown error') })
                : !online
                  ? t('Offline. Run data saved locally — it will sync and be validated when the connection returns.')
                  : sync.syncing
                    ? t('Uploading and validating…')
                    : t('Waiting to sync…')}
            </p>
            {failed ? (
              <Button className="mt-4" variant="secondary" onClick={() => runtime?.sync.retry(local.id)}>
                {t('Retry')}
              </Button>
            ) : null}
          </Card>
        </Page>
      );
    }
    const unfinished = serverRun.data?.status === 'PENDING' && serverRun.data.isOwner ? serverRun.data : null;
    if (unfinished) {
      // Started in the app but never finished from this phone (e.g. recorded on another device or the app was closed).
      return (
        <Page>
          <Header title={t('Unfinished run')} onBack={() => navigate(-1)} />
          <div data-testid="unfinished-run">
            <Card className="mt-4 space-y-3 text-center">
              <StatusPill status="PENDING" />
              <div className="text-sm text-ink-300">{formatRunWhen(unfinished.startedAt)}</div>
              <p className="text-sm text-ink-300">
                {t('This run was started but never finished, so it earned nothing. It also blocks uploading or importing another run at the same time.')}
              </p>
              <Button variant="danger" className="w-full" onClick={() => setDeleting(true)} data-testid="delete-run">
                {t('Delete this run')}
              </Button>
            </Card>
          </div>
          <DeleteRunSheet
            run={unfinished}
            open={deleting}
            onClose={() => setDeleting(false)}
            onDeleted={() => {
              void runtime?.store.deleteRun(unfinished.id);
              navigate('/profile', { replace: true });
            }}
          />
        </Page>
      );
    }
    if (serverRun.isError && !notOnServerYet) return <ErrorState error={serverRun.error} onRetry={() => serverRun.refetch()} />;
    if (serverRun.isPending) return <LoadingState />;
    return <ErrorState error={new ApiError(404, 'Run not found')} />;
  }

  const d = formatDistance(run.distanceM);
  const captured = run.captures.filter((c) => c.ownershipChanged);
  const totalXp = run.xp.reduce((s, x) => s + x.amount, 0);
  const celebrate = fresh && run.status === 'VALID';
  const routePoints = run.route ? run.route.coordinates.flat().map(([lng, lat]) => ({ lat, lng })) : [];
  // Only sectors of the game area count as crossed; a route run elsewhere is shown on the plain map.
  const visited = region ? cellsAlongRoute(region.grid, routePoints).filter((k) => region.isPlayable(k)) : [];
  const leavesRegion = !!region && routePoints.some((p) => !region.contains(p));
  const outsideRegion = run.flags.some((f) => f.code === 'OUTSIDE_REGION');
  const capturedKeys = captured.map((c) => c.gridKey);
  const groups = takenFrom(captured);
  // The viewer's own sectors that this run took, drawn red.
  const lostKeys = run.isOwner ? [] : captured.filter((c) => c.previousOwner?.id === me).map((c) => c.gridKey);
  const focusKeys = focus ? (groups.find((g) => g.key === focus)?.keys ?? []) : null;
  const shownKeys = focusKeys ?? capturedKeys;
  async function makeShareImage() {
    if (!mapHandle || !run?.route || sharing) return;
    setSharing(true);
    try {
      if (hideRouteEnds) {
        setShareRoute(trimRouteEnds(run.route.coordinates, ROUTE_PRIVACY_ZONE_M));
        await new Promise((r) => setTimeout(r, 150));
      }
      await mapIdle(mapHandle);
      const map = await snapshotMap(mapHandle);
      const place = outsideRegion ? (run.placeName ?? '') : (region?.dto.name ?? '');
      setShareImage(
        await drawRunImage({
          map,
          mapLib: mapHandle.lib,
          title: run.title ?? runTitle(run.startedAt),
          subtitle: [run.user.displayName, formatRunWhen(run.startedAt), place].filter(Boolean).join(' · '),
          stats: [
            { label: t('Distance'), value: d.value, unit: d.unit },
            { label: t('Avg pace'), value: formatPace(run.paceSecPerKm), unit: '/km' },
            { label: t('Moving time'), value: formatDuration(run.durationS) },
            { label: t('Territories'), value: String(captured.length), color: '#c6ff3d' },
            { label: t('XP earned'), value: `+${formatNumber(totalXp)}`, color: '#ffc53d' },
            { label: t('Sectors crossed'), value: String(visited.length), color: '#38bdf8' },
          ],
          footer: t('runnerwars.com · Run to conquer your city'),
        }),
      );
    } catch {
      toast({ kind: 'error', title: t('Could not create the image') });
    } finally {
      setShareRoute(null);
      setSharing(false);
    }
  }

  const showFocus = (key: string) => {
    setFocus((f) => (f === key ? null : key));
    appScroller()?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-full pb-nav" data-testid="run-detail">
      {/* Full-bleed map with the tracklog over the territory grid */}
      <div className="relative h-[46dvh] min-h-72 w-full">
        {region && run.route?.coordinates.length ? (
          <GameMap
            key={region.dto.id}
            className="absolute inset-0"
            region={region}
            owners={NO_OWNERS}
            myUserId={me}
            route={shareRoute ?? run.route.coordinates}
            routeVersion={shareRoute ? 2 : 1}
            visited={visited.filter((k) => !shownKeys.includes(k))}
            highlight={shownKeys.filter((k) => !lostKeys.includes(k))}
            lost={shownKeys.filter((k) => lostKeys.includes(k))}
            routeEnds
            fitRoute
            clampToRegion={!leavesRegion}
            controls={false}
            onReady={setMapHandle}
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center bg-ink-850 text-sm text-ink-400">{t('No route recorded')}</div>
        )}
        <button
          onClick={() => (fresh ? navigate('/') : navigate(-1))}
          className="absolute left-3 top-[calc(var(--safe-top)+12px)] grid size-11 place-items-center rounded-full bg-ink-900/85 text-ink-100 shadow-lg backdrop-blur"
          aria-label={t('Back')}
        >
          <svg className="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {run.route ? (
          <div className="absolute right-3 top-[calc(var(--safe-top)+14px)] flex max-w-[70%] flex-wrap justify-end gap-1.5 text-[11px] font-semibold">
            <Legend swatch="bg-[#22c55e] rounded-full" label={t('Start')} />
            <Legend swatch="bg-enemy rounded-full" label={t('Finish')} />
            <Legend swatch="border-2 border-sky" label={t('Crossed')} />
            <Legend swatch="bg-volt/60 border-2 border-volt" label={t('Captured')} />
            {lostKeys.length ? <Legend swatch="bg-enemy/60 border-2 border-enemy" label={t('Yours, lost')} /> : null}
          </div>
        ) : null}
      </div>

      <div className="relative -mt-5 rounded-t-3xl bg-ink-900 px-4 pt-5">
        <div className="mx-auto max-w-lg">
          <div className="flex items-center gap-3">
            <Link to={run.isOwner ? '/profile' : `/players/${run.user.id}`}>
              <Avatar name={run.user.displayName} color={run.isOwner ? MY_COLOR : run.user.color} src={run.user.avatarUrl} />
            </Link>
            <div className="min-w-0 flex-1">
              <Link to={run.isOwner ? '/profile' : `/players/${run.user.id}`} className="block truncate font-semibold" data-testid="run-owner">
                {run.user.displayName}
              </Link>
              <div className="text-xs text-ink-400">
                {formatRunWhen(run.startedAt)} ·{' '}
                <span data-testid="run-place">{outsideRegion
                    ? [run.placeName, t('Outside the game area')].filter(Boolean).join(' · ')
                    : (region?.dto.name ?? run.regionId)}</span>
                {run.source !== 'LIVE' ? (
                  <span className="ml-1 rounded bg-ink-700 px-1.5 py-0.5 text-[10px] font-bold uppercase text-ink-300" data-testid="run-source">
                    {t('Imported')} · {run.source === 'STRAVA' ? 'Strava' : t('File')}
                    {run.deviceName ? ` · ${run.deviceName}` : ''}
                  </span>
                ) : null}
              </div>
            </div>
            <StatusPill status={run.status} />
          </div>

          <h1 className="mt-4 font-display text-3xl font-bold" data-testid="run-title">
            {fresh ? t('Run complete') : (run.title ?? runTitle(run.startedAt))}
          </h1>
          <p className="mt-1 text-sm text-ink-300" data-testid="run-status-text" data-status={run.status}>
            {t(STATUS_TEXT[outsideRegion ? 'OUTSIDE_REGION' : run.status] ?? '')}
          </p>

          {lostKeys.length ? (
            <div className="mt-4 rounded-2xl border border-enemy/40 bg-enemy/10 px-4 py-3 text-sm" data-testid="lost-here">
              <div className="font-semibold text-enemy">
                {t(lostKeys.length === 1 ? '⚔️ You lost 1 sector here' : '⚔️ You lost {n} sectors here', { n: lostKeys.length })}
              </div>
              <div className="mt-0.5 text-ink-300">{t('Marked red on the map. Run through them to take them back!')}</div>
              <Link to={`/rivals/${run.user.id}`} className="mt-2 inline-block font-semibold text-ink-100 underline" data-testid="lost-here-rival">
                {t('You vs {name}', { name: run.user.displayName })}
              </Link>
            </div>
          ) : null}

          {celebrate && captured.length > 0 ? (
            <div className="animate-capture mt-4 rounded-3xl border border-volt/50 bg-volt/10 p-4 text-center" data-testid="capture-celebration">
              <div className="font-display text-5xl font-extrabold text-volt">+{captured.length}</div>
              <div className="font-display text-lg font-bold uppercase tracking-widest">
                {t(captured.length === 1 ? 'Territory conquered' : 'Territories conquered')}
              </div>
            </div>
          ) : null}

          {run.isOwner ? <UnlockedCard unlocked={run.unlocked} fresh={fresh} /> : null}

          <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-5 text-center">
            <Big label={t('Distance')} value={d.value} unit={d.unit} testId="detail-distance" />
            <Big label={t('Avg pace')} value={formatPace(run.paceSecPerKm)} unit="/km" testId="detail-pace" />
            <Big label={t('Moving time')} value={formatDuration(run.durationS)} />
            <Big label={t('Territories')} value={String(captured.length)} accent="text-volt" />
            <Big label={t('XP earned')} value={`+${formatNumber(totalXp)}`} accent="text-gold" testId="run-xp" />
            <Big label={t('Sectors crossed')} value={String(visited.length)} accent="text-sky" testId="sectors-crossed" />
          </div>

          {run.externalUrl ? (
            <a
              href={run.externalUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-block text-sm font-bold text-[#FC5200] underline"
              data-testid="view-on-strava"
            >
              {t('View on Strava')}
            </a>
          ) : null}

          {run.isOwner && run.route?.coordinates.length && run.status !== 'PENDING' ? (
            <Button variant="secondary" className="mt-4 w-full" loading={sharing} disabled={!mapHandle} onClick={makeShareImage} data-testid="run-share">
              {t('Share this run')}
            </Button>
          ) : null}
          <ShareRunSheet image={shareImage} fileName={`runnerwars-${run.startedAt.slice(0, 10)}.png`} onClose={() => setShareImage(null)} />

          {run.routeTrimmed ? (
            <p className="mt-4 rounded-xl bg-ink-850 px-3 py-2 text-center text-xs text-ink-400">
              {t('🔒 This runner hides the first and last 200 m of their routes.')}
            </p>
          ) : null}

          {run.events?.length ? (
            <>
              <SectionTitle>{t('Events')}</SectionTitle>
              <div className="space-y-2" data-testid="run-events">
                {run.events.map((ev) => (
                  <Link key={ev.id} to={`/events/${ev.id}`} className="flex items-center gap-3 rounded-2xl bg-ink-850 px-4 py-3 text-sm">
                    <span className={ev.eligible ? 'text-volt' : 'text-ink-500'}>{ev.eligible ? '✓' : '✕'}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{ev.name}</span>
                      <span className="block text-xs text-ink-400">{ev.eligible ? t('Counted for this event') : t(ev.reason ?? '')}</span>
                    </span>
                    <span className="text-ink-500">›</span>
                  </Link>
                ))}
              </div>
            </>
          ) : null}

          {run.splits.length ? <Splits splits={run.splits} /> : null}

          {groups.some((g) => g.user) ? (
            <>
              <SectionTitle>{t('Taken from')}</SectionTitle>
              <div className="space-y-2" data-testid="taken-from">
                {groups.map((g) => (
                  <button
                    key={g.key}
                    onClick={() => showFocus(g.key)}
                    aria-pressed={focus === g.key}
                    className={`flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 text-left text-sm ${focus === g.key ? 'bg-volt/15 ring-1 ring-volt' : 'bg-ink-850'}`}
                    data-testid="taken-from-row"
                  >
                    {g.user ? (
                      <Avatar name={g.user.displayName} color={g.user.id === me ? MY_COLOR : g.user.color} src={g.user.avatarUrl} size="sm" />
                    ) : (
                      <span className="hex-clip grid size-8 place-items-center bg-ink-700 text-xs text-ink-300">⬡</span>
                    )}
                    <span className="min-w-0 flex-1 truncate font-semibold">
                      {g.user ? (g.user.id === me ? t('You') : g.user.displayName) : t('Unclaimed sectors')}
                    </span>
                    <span className={`font-display text-lg font-bold tabular ${g.user?.id === me ? 'text-enemy' : 'text-volt'}`}>{g.keys.length}</span>
                    <span className="text-xs text-ink-500">{t(focus === g.key ? 'Showing' : 'Show')}</span>
                  </button>
                ))}
              </div>
            </>
          ) : null}

          {run.captures.length ? (
            <>
              <SectionTitle>{t('Sectors')}</SectionTitle>
              <div className="flex flex-wrap gap-2" data-testid="captured-sectors">
                {run.captures.map((c) => (
                  <span
                    key={c.territoryId}
                    className={`rounded-full px-3 py-1 text-xs font-bold ${c.ownershipChanged ? 'bg-volt/15 text-volt' : 'bg-ink-700 text-ink-300'}`}
                  >
                    ⬡ {c.gridKey} {t(c.ownershipChanged ? 'captured' : 'held')}
                  </span>
                ))}
              </div>
            </>
          ) : null}

          {run.xp.length ? (
            <>
              <SectionTitle>{t('XP breakdown')}</SectionTitle>
              <Card className="divide-y divide-ink-800 p-0">
                {run.xp.map((x) => (
                  <div key={x.type} className="flex justify-between px-4 py-3 text-sm">
                    <span className="text-ink-200">{xpLabel(x.label, t)}</span>
                    <span className="font-semibold text-gold tabular">+{x.amount}</span>
                  </div>
                ))}
              </Card>
            </>
          ) : null}

          {run.isOwner && run.flags.length ? (
            <>
              <SectionTitle>{t('Validation notes')}</SectionTitle>
              <Card className="space-y-2 text-sm">
                {run.flags.map((f) => (
                  <div key={f.code} className="flex items-start gap-2">
                    <span className={f.severity === 'info' ? 'text-ink-400' : f.severity === 'reject' ? 'text-enemy' : 'text-warn'}>●</span>
                    <span className="text-ink-300">
                      {t(f.message)} <span className="text-ink-500">({f.count})</span>
                    </span>
                  </div>
                ))}
              </Card>
            </>
          ) : null}

          {fresh ? (
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Link to="/map">
                <Button variant="secondary" className="w-full">
                  {t('View map')}
                </Button>
              </Link>
              <Link to="/leaderboard">
                <Button variant="secondary" className="w-full">
                  {t('Rankings')}
                </Button>
              </Link>
            </div>
          ) : null}

          {run.status !== 'PENDING' ? (
            <>
              <RunKudos runId={run.id} isOwner={run.isOwner} />
              <RunComments runId={run.id} />
            </>
          ) : null}

          {run.isOwner && run.status !== 'PENDING' ? (
            <div className="mt-8 text-center">
              <button className="text-sm font-semibold text-enemy" onClick={() => setDeleting(true)} data-testid="delete-run">
                {t('Delete this run')}
              </button>
            </div>
          ) : null}
          <DeleteRunSheet
            run={run}
            open={deleting}
            onClose={() => setDeleting(false)}
            onDeleted={() => {
              void runtime?.store.deleteRun(run.id);
              navigate(run.source === 'STRAVA' ? '/imports/strava' : '/profile', { replace: true });
            }}
          />
        </div>
      </div>
    </div>
  );
}

const NEUTRAL = 'neutral';

/** Captured sectors grouped by who held them before: rivals first (most taken), unclaimed last. */
function takenFrom(captured: RunDetailDto['captures']): Array<{ key: string; user: PublicUser | null; keys: string[] }> {
  const by = new Map<string, { key: string; user: PublicUser | null; keys: string[] }>();
  for (const c of captured) {
    const key = c.previousOwner?.id ?? NEUTRAL;
    const g = by.get(key) ?? by.set(key, { key, user: c.previousOwner ?? null, keys: [] }).get(key)!;
    g.keys.push(c.gridKey);
  }
  return [...by.values()].sort((a, b) => Number(!a.user) - Number(!b.user) || b.keys.length - a.keys.length);
}

/** XP lines come from the game engine in English, e.g. "2.40 km validated". */
function xpLabel(label: string, t: ReturnType<typeof useT>): string {
  const km = /^([\d.]+) km validated$/.exec(label);
  if (km) return t('{km} km validated', { km: km[1]! });
  const cap = /^(\d+) territor(?:y|ies) captured$/.exec(label);
  if (cap) return t(cap[1] === '1' ? '{n} territory captured' : '{n} territories captured', { n: cap[1]! });
  const sponsored = /^Sponsored sector (\S+) · (.+)$/.exec(label);
  if (sponsored) return t('Sponsored sector {key} · {brand}', { key: sponsored[1]!, brand: sponsored[2]! });
  return t(label);
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 rounded-full bg-ink-900/85 px-2 py-1 backdrop-blur">
      <span className={`size-2.5 ${swatch}`} />
      {label}
    </span>
  );
}

function Splits({ splits }: { splits: RunDetailDto['splits'] }) {
  const paces = splits.map((s) => s.paceSecPerKm);
  const fastest = Math.min(...paces);
  const slowest = Math.max(...paces);
  const t = useT();
  return (
    <>
      <SectionTitle>{t('Splits')}</SectionTitle>
      <Card className="space-y-2.5" >
        <div data-testid="splits">
          {splits.map((s) => {
            // Bar length: faster = longer.
            const w = slowest === fastest ? 100 : 55 + (45 * (slowest - s.paceSecPerKm)) / (slowest - fastest);
            const best = splits.length > 1 && s.paceSecPerKm === fastest && s.complete;
            return (
              <div key={s.index} className="flex items-center gap-3 py-1 text-sm">
                <span className="w-12 shrink-0 font-semibold text-ink-300 tabular">
                  {s.complete ? `${t('Km')} ${s.index}` : `${(s.distanceM / 1000).toFixed(2)}`}
                </span>
                <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-ink-700">
                  <span className={`block h-full rounded-full ${best ? 'bg-volt' : 'bg-sky'}`} style={{ width: `${w}%` }} />
                </span>
                <span className="w-14 shrink-0 text-right font-display text-lg font-bold tabular">{formatPace(s.paceSecPerKm)}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
}

function Header({ title, onBack }: { title: string; onBack: () => void }) {
  const t = useT();
  return (
    <div className="flex items-center gap-2">
      <button onClick={onBack} className="-ml-2 grid size-10 place-items-center rounded-full text-ink-300" aria-label={t('Back')}>
        <svg className="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <h1 className="font-display text-3xl font-bold uppercase">{title}</h1>
    </div>
  );
}

function Big({ label, value, unit, testId, accent }: { label: string; value: string; unit?: string; testId?: string; accent?: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-400">{label}</div>
      <div className={`font-display text-3xl font-bold tabular ${accent ?? ''}`} data-testid={testId}>
        {value}
        {unit ? <span className="ml-1 text-base font-semibold text-ink-400">{unit}</span> : null}
      </div>
    </div>
  );
}
